import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Plus, RefreshCw, AlertTriangle, Wrench, Play, Check, X } from 'lucide-react';
import { Topbar } from '@/components/layout/Topbar';
import { Dialog } from '@/components/ui/Dialog';
import {
  useMaintenances, useMaintenanceAlertes, useCreateMaintenance,
  useDemarrerMaintenance, useTerminerMaintenance, useDeleteMaintenance,
} from '@/hooks/useBetonSprint5';
import { useMoules } from '@/hooks/useBetonCommandes';
import {
  TYPE_MAINTENANCE_MOULE_LABELS, STATUT_MAINTENANCE_MOULE_LABELS,
  type TypeMaintenanceMoule, type StatutMaintenanceMoule, type MaintenanceMoule,
} from '@/types';
import { formatFCFA } from '@/utils/currency';
import { extractError } from '@/api/client';
import { demander } from '@/components/ui/Fenetres';

const fmtDate = (iso: string) => new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' });

const STATUT_COLORS: Record<StatutMaintenanceMoule, string> = {
  planifiee: 'var(--info)',
  en_cours:  'var(--warning)',
  terminee:  'var(--success)',
  annulee:   'var(--tx-3)',
};

export const MaintenanceMoulesPage = () => {
  const [statut, setStatut] = useState<StatutMaintenanceMoule | ''>('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [terminerFor, setTerminerFor] = useState<MaintenanceMoule | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const { data, isLoading, refetch } = useMaintenances({ statut: statut || undefined, limit: 200 });
  const { data: alertes } = useMaintenanceAlertes();
  const demarrer = useDemarrerMaintenance();
  const remove = useDeleteMaintenance();
  const items = data?.data ?? [];

  const kpis = useMemo(() => ({
    planifiee: items.filter((m) => m.statut === 'planifiee').length,
    en_cours:  items.filter((m) => m.statut === 'en_cours').length,
    terminee:  items.filter((m) => m.statut === 'terminee').length,
    alertes:   alertes?.total ?? 0,
  }), [items, alertes]);

  const handle = async (fn: () => Promise<any>, msg?: string) => {
    if (msg && !(await demander.confirmer({ titre: msg, danger: /supprim|annul|rejet|refus|désactiv|écart/i.test(msg) }))) return;
    setActionError(null);
    try { await fn(); } catch (e) { setActionError(extractError(e).message); }
  };

  return (
    <>
      <Topbar
        title="Maintenance moules"
        subtitle="Module 12 — Entretien préventif (nettoyage/huilage/contrôle géométrie/réparation) + alertes cycles"
        actions={
          <>
            <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => void refetch()}>
              <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
            </button>
            <button className="ds-btn ds-btn-p" onClick={() => setDialogOpen(true)}>
              <Plus size={14} /> Planifier entretien
            </button>
          </>
        }
      />
      <div className="ds-content">
        {actionError && (
          <div className="ds-alert-strip ds-alert-d" style={{ marginBottom: 16 }}>
            <span className="ds-alert-ico"><AlertTriangle size={18} /></span>
            <div className="ds-alert-txt">{actionError}</div>
          </div>
        )}

        <div className="ds-kpi-strip">
          <div className="ds-ks ks-blue">
            <div className="ds-ks-label">Planifiées</div>
            <div className="ds-ks-value">{kpis.planifiee}</div>
            <div className="ds-ks-sub">À démarrer</div>
          </div>
          <div className="ds-ks ks-orange">
            <div className="ds-ks-label">En cours</div>
            <div className="ds-ks-value">{kpis.en_cours}</div>
            <div className="ds-ks-sub">Atelier mobilisé</div>
          </div>
          <div className="ds-ks ks-green">
            <div className="ds-ks-label">Terminées</div>
            <div className="ds-ks-value">{kpis.terminee}</div>
            <div className="ds-ks-sub">Historique</div>
          </div>
          <div className={`ds-ks ${kpis.alertes > 0 ? 'ks-red' : 'ks-green'}`}>
            <div className="ds-ks-label">Alertes</div>
            <div className="ds-ks-value">{kpis.alertes}</div>
            <div className="ds-ks-sub">Moules à entretenir</div>
          </div>
        </div>

        {alertes && alertes.total > 0 && (
          <div className="ds-alert-strip ds-alert-w" style={{ marginBottom: 14 }}>
            <span className="ds-alert-ico"><AlertTriangle size={18} /></span>
            <div className="ds-alert-txt">
              <strong>{alertes.total} moule{alertes.total > 1 ? 's' : ''} sans maintenance planifiée :</strong>{' '}
              {alertes.alertes.slice(0, 3).map((a) => `${a.code} (${a.cycles}/${a.seuil} +${a.depassement})`).join(' · ')}
              {alertes.total > 3 && ` · +${alertes.total - 3} autre${alertes.total - 3 > 1 ? 's' : ''}`}
            </div>
          </div>
        )}

        <div className="ds-filters">
          <select className="ds-fi" value={statut} onChange={(e) => setStatut(e.target.value as StatutMaintenanceMoule | '')} style={{ width: 'auto' }}>
            <option value="">Tous statuts</option>
            {(Object.keys(STATUT_MAINTENANCE_MOULE_LABELS) as StatutMaintenanceMoule[]).map((s) => (
              <option key={s} value={s}>{STATUT_MAINTENANCE_MOULE_LABELS[s]}</option>
            ))}
          </select>
        </div>

        <div className="ds-table-panel">
          <div className="ds-table-header">
            <div>
              <div className="ds-table-title">Maintenances planifiées</div>
              <div className="ds-table-sub">{items.length} entretien{items.length > 1 ? 's' : ''}</div>
            </div>
          </div>
          <table className="ds-tbl">
            <thead>
              <tr>
                <th style={{ width: 120 }}>Référence</th>
                <th style={{ width: 130 }}>Moule</th>
                <th style={{ width: 130 }}>Type</th>
                <th>Description</th>
                <th style={{ width: 110 }}>Planifiée</th>
                <th style={{ width: 110 }}>Statut</th>
                <th className="ds-am" style={{ width: 100 }}>Coût</th>
                <th style={{ width: 170 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={8} style={{ textAlign: 'center', padding: 32 }}>Chargement…</td></tr>}
              {!isLoading && items.length === 0 && (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: 48 }}>
                  <Wrench size={28} style={{ margin: '0 auto 8px', color: 'var(--tx-3)' }} />
                  <p style={{ fontSize: 13, color: 'var(--tx-3)' }}>Aucune maintenance</p>
                </td></tr>
              )}
              {!isLoading && items.map((m) => (
                <tr key={m.id}>
                  <td><span className="ds-ref-code">{m.reference}</span></td>
                  <td>
                    <div className="ds-entry-label">{m.moule?.code ?? '—'}</div>
                    <div className="ds-entry-sub">{m.moule?.libelle}</div>
                  </td>
                  <td><span className="ds-bd ds-bd-i" style={{ fontSize: 10 }}>{TYPE_MAINTENANCE_MOULE_LABELS[m.type]}</span></td>
                  <td style={{ fontSize: 11 }}>
                    {m.description.slice(0, 80)}{m.description.length > 80 ? '…' : ''}
                    {m.technicienNom && <div style={{ fontSize: 10, color: 'var(--tx-3)' }}>Tech: {m.technicienNom}</div>}
                  </td>
                  <td style={{ fontSize: 11, color: 'var(--tx-2)' }}>{fmtDate(m.datePlanifiee)}</td>
                  <td>
                    <span className="ds-bd" style={{ background: STATUT_COLORS[m.statut], color: 'white', fontSize: 10 }}>
                      {STATUT_MAINTENANCE_MOULE_LABELS[m.statut]}
                    </span>
                  </td>
                  <td className="ds-am" style={{ fontSize: 11 }}>{m.coutCents ? formatFCFA(BigInt(m.coutCents)) : '—'}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {m.statut === 'planifiee' && (
                        <>
                          <button className="ds-btn ds-btn-p ds-btn-sm" onClick={() => handle(() => demarrer.mutateAsync(m.id), `Démarrer ${m.reference} ?`)} title="Démarrer">
                            <Play size={11} /> Démarrer
                          </button>
                          <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => handle(() => remove.mutateAsync(m.id), `Supprimer ${m.reference} ?`)} style={{ color: 'var(--danger)' }}>
                            <X size={11} />
                          </button>
                        </>
                      )}
                      {m.statut === 'en_cours' && (
                        <button className="ds-btn ds-btn-p ds-btn-sm" onClick={() => setTerminerFor(m)} title="Terminer">
                          <Check size={11} /> Terminer
                        </button>
                      )}
                      {(m.statut === 'terminee' || m.statut === 'annulee') && (
                        <span style={{ fontSize: 10, color: 'var(--tx-3)' }}>
                          {m.dateFin ? fmtDate(m.dateFin) : '—'}
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <MaintenanceFormDialog open={dialogOpen} onClose={() => setDialogOpen(false)} alerteFor={null} />
      <TerminerDialog maintenance={terminerFor} onClose={() => setTerminerFor(null)} />
    </>
  );
};

const MaintenanceFormDialog = ({ open, onClose, alerteFor }: { open: boolean; onClose: () => void; alerteFor: string | null }) => {
  const [mouleId, setMouleId] = useState('');
  const [type, setType] = useState<TypeMaintenanceMoule>('nettoyage');
  const [datePlanifiee, setDP] = useState(new Date().toISOString().slice(0, 10));
  const [description, setDescr] = useState('');
  const [technicien, setTech] = useState('');
  const [cout, setCout] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data: moulesData } = useMoules({ limit: 200 });
  const create = useCreateMaintenance();

  useEffect(() => {
    if (open) {
      setMouleId(alerteFor ?? ''); setType('nettoyage');
      setDP(new Date().toISOString().slice(0, 10));
      setDescr(''); setTech(''); setCout(''); setError(null);
    }
  }, [open, alerteFor]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await create.mutateAsync({
        mouleId, type, datePlanifiee, description: description.trim(),
        technicienNom: technicien.trim() || undefined,
        coutCents: cout ? parseInt(cout, 10) : undefined,
      });
      onClose();
    } catch (err) { setError(extractError(err).message); }
  };

  return (
    <Dialog open={open} onClose={onClose} title="Planifier maintenance moule" subtitle="Réf auto MM-YYYY-NNNN · statut Planifiée" size="md">
      <form onSubmit={onSubmit} className="space-y-3">
        {error && <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{error}</div>}

        <div className="grid grid-cols-2 gap-3">
          <Field label="Moule *">
            <select value={mouleId} onChange={(e) => setMouleId(e.target.value)} required className="ds-fi" style={{ width: '100%' }}>
              <option value="">— Sélectionner —</option>
              {(moulesData?.data ?? []).map((m) => (
                <option key={m.id} value={m.id}>
                  {m.code} · {m.libelle} ({m.nbCyclesEffectues}/{m.prochainEntretienCycles ?? '∞'} cycles)
                </option>
              ))}
            </select>
          </Field>
          <Field label="Type *">
            <select value={type} onChange={(e) => setType(e.target.value as TypeMaintenanceMoule)} className="ds-fi" style={{ width: '100%' }}>
              {(Object.keys(TYPE_MAINTENANCE_MOULE_LABELS) as TypeMaintenanceMoule[]).map((t) => (
                <option key={t} value={t}>{TYPE_MAINTENANCE_MOULE_LABELS[t]}</option>
              ))}
            </select>
          </Field>
        </div>

        <Field label="Date planifiée *">
          <input type="date" value={datePlanifiee} onChange={(e) => setDP(e.target.value)} required className="ds-fi" style={{ width: 200 }} />
        </Field>

        <Field label="Description *">
          <textarea value={description} onChange={(e) => setDescr(e.target.value)} required rows={3} className="ds-fi" style={{ width: '100%' }} placeholder="Nettoyage haute pression + huilage anti-rouille + remplacement joints..." />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Technicien"><input value={technicien} onChange={(e) => setTech(e.target.value)} className="ds-fi" style={{ width: '100%' }} /></Field>
          <Field label="Coût estimé (cents)"><input type="number" min="0" value={cout} onChange={(e) => setCout(e.target.value)} className="ds-fi" style={{ width: '100%' }} placeholder="ex: 25000000" /></Field>
        </div>

        <div className="flex justify-end gap-2 border-t pt-3">
          <button type="button" className="ds-btn ds-btn-g" onClick={onClose}>Annuler</button>
          <button type="submit" className="ds-btn ds-btn-p" disabled={!mouleId || !description.trim() || create.isPending}>
            <Plus size={14} /> Planifier
          </button>
        </div>
      </form>
    </Dialog>
  );
};

const TerminerDialog = ({ maintenance, onClose }: { maintenance: MaintenanceMoule | null; onClose: () => void }) => {
  const open = maintenance !== null;
  const terminer = useTerminerMaintenance();
  const [resultat, setResultat] = useState('');
  const [coutReel, setCR] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && maintenance) {
      setResultat(maintenance.resultat ?? '');
      setCR(maintenance.coutCents ?? '');
      setError(null);
    }
  }, [open, maintenance]);

  if (!maintenance) return null;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await terminer.mutateAsync({
        id: maintenance.id,
        payload: { resultat: resultat.trim() || undefined, coutCents: coutReel ? parseInt(coutReel, 10) : undefined },
      });
      onClose();
    } catch (err) { setError(extractError(err).message); }
  };

  return (
    <Dialog open={open} onClose={onClose} title={`Terminer ${maintenance.reference}`} subtitle={`Moule ${maintenance.moule?.code} · ${TYPE_MAINTENANCE_MOULE_LABELS[maintenance.type]}`} size="sm">
      <form onSubmit={onSubmit} className="space-y-3">
        {error && <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{error}</div>}

        <Field label="Résultat / Compte-rendu">
          <textarea value={resultat} onChange={(e) => setResultat(e.target.value)} rows={3} className="ds-fi" style={{ width: '100%' }} placeholder="Travaux effectués, observations, pièces remplacées..." />
        </Field>

        <Field label="Coût réel (cents)">
          <input type="number" min="0" value={coutReel} onChange={(e) => setCR(e.target.value)} className="ds-fi" style={{ width: '100%' }} />
        </Field>

        <div className="rounded-md border border-emerald-200 bg-emerald-50 p-2 text-xs text-emerald-800">
          ⚠ Cette action verrouille la maintenance + remet le moule en operationnel + reset compteur cycles (entretien préventif).
        </div>

        <div className="flex justify-end gap-2 border-t pt-3">
          <button type="button" className="ds-btn ds-btn-g" onClick={onClose}>Annuler</button>
          <button type="submit" className="ds-btn ds-btn-p" disabled={terminer.isPending}>
            <Check size={14} /> Valider terminée
          </button>
        </div>
      </form>
    </Dialog>
  );
};

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div>
    <label className="mb-1 block text-xs font-medium text-gray-600">{label}</label>
    {children}
  </div>
);
